import { formatPrice } from '@/common/constant';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import {
  BarChartOutlined,
  DeleteOutlined,
  EditOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Space, Switch, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Menu } from '../types';

interface MenuColumnsProps {
  handleStatusChange: (checked: boolean, record: Menu) => Promise<void>;
  handleDelete: (records: Menu[]) => Promise<void>;
  showEditModal: (record: Menu) => void;
  showAnalysisModal: (record: Menu) => void;
  loading: boolean;
  regeneratePairing: (menu: Menu) => Promise<void>;
}

export const getMenuColumns = ({
  handleStatusChange,
  handleDelete,
  showEditModal,
  showAnalysisModal,
  loading,
  regeneratePairing,
}: MenuColumnsProps): ColumnsType<Menu> => {
  const { currency } = useCurrencySymbol();

  return [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record) => (
        <Switch
          checked={status === 'enabled'}
          onChange={(checked) => handleStatusChange(checked, record)}
          loading={loading}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) =>
        formatPrice(Number.parseFloat(price as string), currency),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[] | null) => (
        <div className="flex flex-wrap gap-1">
          {tags?.map((tag) => <Tag key={tag}>{tag}</Tag>) || '-'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      className: 'text-right',
      render: (_, record) => (
        <Space>
          {record.pairing != null ? (
            record.pairing.status === 'completed' &&
            record.pairing.analysis_data?.dish_analysis != null ? (
              <>
                <Button
                  icon={<BarChartOutlined />}
                  type="text"
                  onClick={() => showAnalysisModal(record)}
                >
                  Analysis
                </Button>
                <Tooltip title="Regenerate Pairing">
                  <Button
                    icon={<SyncOutlined />}
                    type="text"
                    onClick={() => regeneratePairing(record)}
                  />
                </Tooltip>
              </>
            ) : record.pairing.status === 'pending' ||
              record.pairing.status === 'processing' ? (
              <Button icon={<SyncOutlined spin />} type="text" disabled>
                Processing...
              </Button>
            ) : record.pairing.status === 'failed' ? (
              <Tooltip title="Regenerate Pairing">
                <Button
                  icon={<SyncOutlined />}
                  type="primary"
                  danger
                  onClick={() => regeneratePairing(record)}
                  loading={loading}
                />
              </Tooltip>
            ) : null
          ) : (
            <Tooltip title="Generate Pairing">
              <Button
                icon={<ThunderboltOutlined />}
                type="primary"
                onClick={() => regeneratePairing(record)}
                loading={loading}
              />
            </Tooltip>
          )}
          <Button
            icon={<EditOutlined />}
            type="text"
            onClick={() => showEditModal(record)}
          />
          <Popconfirm
            title="Delete dish"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete([record])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];
};
